const $ = jQuery,
    RES = window.RES

import shared from '../shared'
import {EventEmitter} from 'events'
import SimpleBar from 'simplebar'


export default class Search extends EventEmitter {
    constructor($container, urlMode = false, resultsPerQuery = 5) {
        super()
        this.container = $container
        this.root = $(Search.layout)
        this.container.append(this.root)
        this.urlMode = urlMode

        this.head = this.find('header')
        this.input = this.find('input')
        this.results = this.find('results')
        this.resultsWrapper = this.find('results-wrapper')
        this.message = this.find('message')
        this.pageLink = this.find('message-pageLink')

        this.resultsScrollBar = new SimpleBar(this.resultsWrapper.get(0))

        this.partials = []

        this._locked = false
        this._lastS = ''

        this.resultsPerQuery = resultsPerQuery

        this.bindControls()

        if (this.urlMode) {
            let urlParam = shared.urlTools.read('search') ? 'search' : 's'
            this.s = shared.urlTools.read(urlParam) || ''

            this.on('searchSuccess', () => {
                shared.urlTools.write(urlParam, this.s, { s: this.s })
            })

            window.addEventListener('popstate', event => {
                this.s = history.state && history.state.s
                         ? history.state.s
                         : shared.urlTools.read(urlParam) || this._lastS
            })

            this.pageLink.hide()
        }

    }

    dismissKeyboard() {
        if (this._lastS === this.input.val()) {
            this.input.trigger('blur')
        }
    }

    get s() {
        return this.input.val()
    }

    set s(val) {
        if (typeof val === 'string' && val !== this._lastS) {
            this.input.val(val)
            if (this.input.val()) {
                this.pageLink.attr('href', `/?s=${encodeURIComponent(this.input.val())}`)
                this.pageLink.removeClass('s-empty')
            } else {
                this.pageLink.attr('href', ``)
                this.pageLink.addClass('s-empty')
            }
            this.scheduleSearch()
        }
    }

    get locked() { return this._locked }

    get changed() { return this._lastS !== this.s }

    updateMessage(message) {
        this.message.find('.lookup-message-body').html(message)
    }

    lock() {
        this._locked = true
        this.updateMessage(RES.__('search', 'searching'))
        this.root.addClass('s-searching')
    }

    unlock() {
        this._locked = false
        this.root.removeClass('s-searching')
    }

    clear() {
        this.s = ''
    }

    find(subSelector) {
        return this.root.find(`.lookup-${subSelector}`)
    }

    bindControls() {
        this.input.on('keyup', event => {
            if (event.key === 'Escape') {
                this.emit('close')
            } else if (event.key === 'Enter') {
                if (this.urlMode) {
                    this.scheduleSearch()
                } else {
                    window.open(`/?s=${this.s}`, '_self')
                }
            } else {
                this.s = this.input.val()
            }
        })
    }

    checkForChanges() {
        if (this.changed) {
            this.scheduleSearch()
        }
    }

    renderPartials() {
        this.results.empty()
        this.partials.forEach(partial => {
            partial.on('viewMore', ({ offset }) => {
                this.schedulePartialSearch(offset, partial)
            })
            if (partial.hasResults()) {
                this.results.append(partial.markup)
            }
        })
        this.updatePartialsNumbers()
        this.recalculateResultsHeight()
    }

    recalculateResultsHeight() {

        this.resultsScrollBar.recalculate()
    }

    updatePartialsNumbers() {
        let showing = 0,
            found = 0
        this.partials.forEach(partial => {
            showing += partial.list.length
            found += partial.found
        })
        if (found > showing) {
            this.updateMessage(RES.__('search', 'showing_n_of_m_results').replace('%n', showing).replace('%m', found))
        } else if (found === 1) {
            this.updateMessage(RES.__('search', 'found_one_result'))
        } else if (found > 1) {
            this.updateMessage(RES.__('search', 'found_n_results').replace('%n', found))
        } else if (this.s) {
            this.updateMessage(RES.__('search', 'start_typing'))
        } else {
            this.updateMessage(RES.__('search', 'nothing_found'))
        }
    }

    onServerSuccess(payload = []) {
        this.partials = payload
            .filter(part => part.list.length)
            .map(part => new Partial(part))
        this.renderPartials()
        this.emit('searchSuccess', { payload })
    }

    onServerFailure() {
        this.updateMessage(RES.__('search', 'search_failed'))
        this.emit('searchFailure')
    }

    schedulePartialSearch(offset, partial) {
        if (this.locked) {
            return
        }
        this.lock()
        this.emit('partialSearchStart', {
            offset,
            partial,
        })
        $.ajax({
            url: RES.global('ajaxurl'),
            method: 'POST',
            data: {
                action: 'WA_WP_SearchHandler',
                method: 'partialSearch',
                partial: partial.name,
                query: {
                    s: this.s,
                    posts_per_page: this.resultsPerQuery,
                    offset: offset,
                },
            },
        }).done(data => {
            if (data && data.success) {
                partial.insertMoreResults(data.payload.list)
            } else {
                partial.onServerFailure()
            }
            this.updatePartialsNumbers()
            this.emit('searchEnd')
            this.emit('partialSearchEnd')
        }).fail((jqXHR, status, code) => {
            partial.onServerFailure()
            this.emit('searchEnd')
            this.emit('partialSearchEnd')
        }).always(() => {
            this.unlock()
        })
    }

    scheduleSearch(oncomplete) {
        if (this.locked || !this.changed) {
            return
        }
        this.lock()
        this._lastS = this.s
        this.emit('searchStart')
        $.ajax({
            url: RES.global('ajaxurl'),
            method: 'POST',
            data: {
                action: 'WA_WP_SearchHandler',
                method: 'search',
                query: {
                    s: this.s,
                    posts_per_page: this.resultsPerQuery,
                },
            },
        }).done(data => {
            if (data && data.success) {
                this.onServerSuccess(data.payload)
            } else {
                this.onServerFailure()
            }
            this.emit('searchEnd')
        }).fail((jqXHR, status, code) => {
            this.onServerFailure()
            this.emit('searchEnd')
        }).always(() => {
            this.unlock()
            this.checkForChanges()
            if (typeof oncomplete === 'function') {
                oncomplete()
            }
        })
    }

    static get layout() {
        return `
          <div class="lookup">
            <header class="lookup-header">
              <input class="lookup-input" type="text" placeholder="${RES.__('search', 'search_this_website')}"/>
              <i class="fa-regular fa-search lookup-header-icon" aria-hidden="true"></i>
              <div class="lookup-message-wrapper">
                <p class="lookup-message">
                  <i class="fa-regular fa-circle-notch icon-spinner fa-spin" aria-hidden="true"></i>
                  <span class="lookup-message-body">${RES.__('search', 'start_typing')}</span>
                  <a class="lookup-message-pageLink s-empty" target="_blank">
                    ${RES.__('search', 'open_search_page')}
                    <i class="fa-regular fa-external-link" aria-hidden="true"></i>
                  </a>
                </p>
              </div>
            </header>
            <div class="lookup-results-wrapper">
              <div class="lookup-results">
              </div>
            </div>
          </div>
        `
    }
}


export class Partial extends EventEmitter {
    constructor(data) {
        super()
        this.name = data.name || ''
        this.title = data.title || ''
        this.list = data.list || []
        this.found = data.found || 0

        this.markup = $(`
          <div class="lookup-partial">
            <h2 class="lookup-partial-title">${this.title}</h2>
            <div class="lookup-partial-list">${this.list.join('')}</div>
          </div>
        `)

        this.viewMore = $(`
          <button class="lookup-partial-viewMore">
            <i class="fa-regular fa-circle-notch icon-spinner fa-spin" aria-hidden="true"></i>
            <span class="text-searching">${RES.__('search', 'searching')}</span>
            <span class="text-more">${RES.__('search', 'load_more_results')}</span>
            <i class="fa-regular fa-angle-down icon-more" aria-hidden="true"></i>
          </button>
        `)

        this.viewMore.on('click', () => {
            this.emit('viewMore', {
                offset: this.list.length,
            })
        })

        if (this.found > this.list.length) {
            this.markup.append(this.viewMore)
        }

    }

    hasResults() {
        return this.list.length > 0
    }

    insertMoreResults(results) {
        if (!results || !results.length) {
            return false
        }
        this.list = this.list.concat(results)
        this.markup.find('.lookup-partial-list').append(results.join(''))
        if (this.found <= this.list.length) {
            this.viewMore.detach()
        }
    }

    onServerFailure() {

    }
}
